import { measureTextLayerBounds } from '@core/textMeasurement';
import { sanitizeDefaultTextLayer, type DefaultTextLayerConfig } from './config';
import { ensureDefaultTextLayerFontLoaded } from './fontLoader';

type DesignItem = {
  id?: string;
  type?: string;
  text?: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
};

type VeyopFacade = {
  hooks?: {
    on: (event: string, handler: (payload: unknown) => void) => void;
  };
};

let registered = false;
let designerConfig: Record<string, unknown> = readDesignerConfig();

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readDesignerConfig(): Record<string, unknown> {
  const el = document.getElementById('designer-config');
  if (!el) return {};
  try {
    const parsed = JSON.parse(el.textContent || '{}');
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function defaultLayerFromConfig(): DefaultTextLayerConfig {
  const direct = designerConfig.defaultTextLayer;
  if (isRecord(direct)) {
    return sanitizeDefaultTextLayer(direct);
  }

  const settings = designerConfig.settings;
  if (isRecord(settings) && isRecord(settings.default_text_layer)) {
    return sanitizeDefaultTextLayer(settings.default_text_layer);
  }

  return sanitizeDefaultTextLayer({});
}

function veyopBaseUrl(): string {
  return typeof designerConfig.veyopUrl === 'string' ? designerConfig.veyopUrl : '';
}

function measureDefaultLayer(config: DefaultTextLayerConfig): Pick<DesignItem, 'width' | 'height'> {
  const nextText = config.text || 'Add text';
  const fontFamily = String(config.fontFamily || 'Arial');
  const fontSize = Number(config.fontSize || 24);
  const fontWeight = String(config.fontWeight ?? 'normal');
  const fontStyle = String(config.fontStyle ?? 'normal');
  const lineHeight = Number(config.lineHeight || 1.2);
  const letterSpacing = Number(config.letterSpacing || 0);
  const textDecoration = String(config.textDecoration ?? 'none');
  const textAlign = String(config.textAlign ?? 'center');
  const minW = 44;
  const bounds = measureTextLayerBounds(nextText, fontFamily, fontSize, {
    fontWeight,
    fontStyle,
    lineHeight,
    letterSpacing,
    textDecoration,
    textAlign,
  });
  return {
    width: Math.max(minW, bounds.width),
    height: Math.max(32, bounds.height),
  };
}

function configPatch(config: DefaultTextLayerConfig): Partial<DesignItem> {
  return {
    text: config.text || 'Add text',
    fontFamily: config.fontFamily,
    fontSize: config.fontSize,
    fontWeight: config.fontWeight,
    fontStyle: config.fontStyle,
    textDecoration: config.textDecoration,
    textAlign: config.textAlign,
    color: config.color,
    outlineColor: config.outlineColor,
    outlineWidth: config.outlineWidth,
    letterSpacing: config.letterSpacing,
    lineHeight: config.lineHeight,
    opacity: config.opacity,
    ...measureDefaultLayer(config),
  };
}

function loadDefaultLayerFont(config: DefaultTextLayerConfig): void {
  void ensureDefaultTextLayerFontLoaded(config.fontFamily, veyopBaseUrl())
    .catch(() => undefined);
}

function applyDefaultLayer(item: DesignItem): void {
  if (!item || item.type !== 'text') return;

  // The sidebar Add Text button creates exactly this starter string. Text style
  // cards pass their own text, so they keep their authored styles.
  if (String(item.text ?? '') !== 'Add text') return;

  const config = defaultLayerFromConfig();
  Object.assign(item, configPatch(config));
  // Font loading must not create a second design mutation: otherwise a quick
  // Undo first reverts the async re-measure instead of removing the new layer.
  loadDefaultLayerFont(config);
}

function register(Veyop: VeyopFacade): void {
  if (registered || !Veyop.hooks?.on) return;
  registered = true;

  Veyop.hooks.on('veyop:designer:ready', (payload) => {
    if (isRecord(payload) && isRecord(payload.config)) {
      designerConfig = payload.config;
    } else {
      designerConfig = readDesignerConfig();
    }
  });

  Veyop.hooks.on('after.add.text.design', (payload) => {
    if (!isRecord(payload) || !isRecord(payload.item)) return;
    applyDefaultLayer(payload.item as DesignItem);
  });
}

function boot(): void {
  const tryRegister = () => {
    const Veyop = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
    if (Veyop?.hooks) {
      register(Veyop);
    }
  };

  tryRegister();

  const start = Date.now();
  const tick = () => {
    tryRegister();
    if (registered || Date.now() - start > 5000) return;
    setTimeout(tick, 50);
  };
  tick();
}

boot();
